import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CorrectionFieldTags from '../../review/form/CorrectionFieldTags';
import DataFormatSelect from '../../common/DataFormatSelect';
import DraggablePaper from '../../common/DraggablePaper';
import FieldTags from '../../fields/FieldTags';
import errorResponses from '../../../errorResponses';
import { AppContext } from '../../../App';
import {
  DataTypeOption,
  FIELD_DATA_TYPE_CONFIG_DEFAULT,
  parseCompositeFormatKey,
} from '../../fields/FieldsSettings/FieldsSettingsTypes';
import { FieldModalProps } from './FieldModalTypes';
import {
  DialogFieldStyled,
  DivDialogTitle,
  DivExtraMargin,
  DivFlexChild,
  DivFlexContainer,
  DivFlexEnd,
  InputLabelDataType,
  SelectFormControl,
} from './FieldStyles';
import { FormValues } from '../form/MarkFieldTypes';
import { MarkDispatchContext } from '../../../context/MarkContext';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import { doesStringContainPeriod } from '../../../utils/doesStringContainPeriod';
import { formatDataTypeAndConfig } from '../../fields/utils/utilsFields';
import { getConfigByDataType } from '../../../utils/getConfigByDataType';
import { getDefaultDataFormat } from '../../../utils/getDefaultDataFormat';
import { useCreateCorrection } from '../../hooks/useCreateCorrection';
import { useFieldUpdate } from '../../hooks/useFieldUpdate';
import { useQueryClient } from '@tanstack/react-query';
import {
  addCurrentChargesSumCheck,
  addRequiredFieldValidator,
  fieldHasCurrentChargesSumCheck,
  getExistingSumcheckConfig,
  isCustomSumcheckConfig,
  isFieldRequired,
  removeCurrentChargesSumCheck,
  removeRequiredFieldValidator,
  DEFAULT_SUMCHECK_CONFIG,
} from '../../../utils/fieldValidations';
import DeleteField from '../../fields/DeleteField';
import ChargesSumcheckConfigDialog from './ChargesSumcheckConfigDialog';

import { featureFlags } from '../../../utils/featureFlags';

// FieldModal component for modifying field properties
const FieldModal: React.FC<FieldModalProps> = ({
  dataTypesResponse,
  ebId,
  tsId,
  extractionId,
  field,
  fieldDataType,
  fieldDataTypeConfig,
  fieldId,
  glyntTags,
  initValue,
  isAdditionalField,
  isReview = false,
  openModal,
  setOpenModal,
  setUpdateFieldListLabel,
  validationConfig,
}) => {
  const { setToast } = useContext(AppContext);
  const isCurrentlyRequired = isFieldRequired(validationConfig ?? null);
  const isCurrentChargesSumCheck = fieldHasCurrentChargesSumCheck(validationConfig ?? null);
  const existingSumcheckConfig = getExistingSumcheckConfig(validationConfig ?? null);

  const dispatch = useContext<React.Dispatch<any>>(MarkDispatchContext);
  const [fieldError, setFieldError] = React.useState('');
  const [sumcheckConfigDialogOpen, setSumcheckConfigDialogOpen] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const prevOpenModalRef = useRef(false);
  const queryClient = useQueryClient();

  // Initialize form values
  const [formValues, setFormValues] = React.useState<FormValues>({
    fieldName: field,
    dataType: fieldDataType,
    dataFormat: fieldDataTypeConfig,
    fieldValue: initValue,
    fieldRequired: isCurrentlyRequired,
    currentChargesSumCheck: isCurrentChargesSumCheck,
    sumcheckConfig: existingSumcheckConfig,
  });

  // Track initial values to detect unsaved changes
  const [initialFormValues, setInitialFormValues] = React.useState<FormValues>({
    fieldName: field,
    dataType: fieldDataType,
    dataFormat: fieldDataTypeConfig,
    fieldValue: initValue,
    fieldRequired: isCurrentlyRequired,
    currentChargesSumCheck: isCurrentChargesSumCheck,
    sumcheckConfig: existingSumcheckConfig,
  });

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    if (formValues.fieldName !== initialFormValues.fieldName) return true;
    if (formValues.dataType !== initialFormValues.dataType) return true;
    if (formValues.fieldValue !== initialFormValues.fieldValue) return true;
    if (formValues.fieldRequired !== initialFormValues.fieldRequired) return true;
    if (formValues.currentChargesSumCheck !== initialFormValues.currentChargesSumCheck) return true;
    // Compare sumcheckConfig
    if (
      JSON.stringify(formValues.sumcheckConfig) !== JSON.stringify(initialFormValues.sumcheckConfig)
    )
      return true;
    // Compare dataFormat
    if (JSON.stringify(formValues.dataFormat) !== JSON.stringify(initialFormValues.dataFormat))
      return true;
    return false;
  }, [formValues, initialFormValues]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setUnsavedChangesDialogOpen(true);
    } else {
      setOpenModal(false);
    }
  }, [hasUnsavedChanges, setOpenModal]);

  const handleConfirmClose = () => {
    setUnsavedChangesDialogOpen(false);
    setOpenModal(false);
  };

  // Determine if the current config differs from the default
  const hasCustomSumcheckConfig = isCustomSumcheckConfig(formValues.sumcheckConfig);

  const fieldUpdateMutation = useFieldUpdate({
    onSuccess: () => {
      setToast({
        open: true,
        message: 'Field Updated.',
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['fields']
      });
      setOpenModal(false);
    },
    onError: () => {
      setToast({
        open: true,
        message: errorResponses.failedToRenameField,
        variant: 'error',
      });
    },
  }, tsId);

  const createCorrectionMutation = useCreateCorrection(true, true, {
    onSuccess: () => {
      if (isReview) {
        setOpenModal(false);
        dispatch({ type: 'RESET_TOKENS' });
        queryClient.invalidateQueries({
          queryKey: ['extraction']
        });
        setUpdateFieldListLabel && setUpdateFieldListLabel(true);
        setToast({
          open: true,
          message: 'Correction Updated.',
          variant: 'success',
        });
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries({
        queryKey: ['extraction', ebId]
      });
      console.error('ERROR', error);
      setToast({
        open: true,
        message: `Error: ${error?.message || 'Error during correction update'}`,
        variant: 'error',
      });
    },
  });

  // Update form values when modal is opened (only on transition from closed to open)
  useEffect(() => {
    if (openModal && !prevOpenModalRef.current) {
      const newValues = {
        fieldName: field,
        dataType: fieldDataType,
        dataFormat: fieldDataTypeConfig,
        fieldValue: initValue,
        fieldRequired: isCurrentlyRequired,
        currentChargesSumCheck: isCurrentChargesSumCheck,
        sumcheckConfig: existingSumcheckConfig,
      };
      setFormValues(newValues);
      setInitialFormValues(newValues);
    }
    prevOpenModalRef.current = openModal;
  }, [
    openModal,
    field,
    fieldDataType,
    fieldDataTypeConfig,
    initValue,
    isCurrentlyRequired,
    isCurrentChargesSumCheck,
    existingSumcheckConfig,
  ]);

  // Update data format when data type changes
  useEffect(() => {
    let defaultDataFormat: DataTypeOption | {} = {};

    // Check if formValues.dataType is set
    if (formValues.dataType) {
      // Find options for the selected data type
      const dataTypeOptions = dataTypesResponse?.find(
        (dt) => dt.name === formValues.dataType
      )?.options;

      // Get default config for the selected data type
      // And compute the default data format based on the selected data type and its default config
      const defaultConfig = FIELD_DATA_TYPE_CONFIG_DEFAULT[formValues.dataType];
      defaultDataFormat = getDefaultDataFormat(formValues.dataType, defaultConfig, dataTypeOptions);
    }

    // Update the form values to set the new default data format
    setFormValues((prevValues) => ({
      ...prevValues,
      dataFormat: defaultDataFormat,
    }));
  }, [formValues.dataType, dataTypesResponse]);

  // Build common data for API payload
  const buildCommonData = (fieldId: string, fieldName: string, dataType?: string) => ({
    id: fieldId,
    data: {
      label: fieldName,
      data_type: dataType,
    },
  });

  // Handle form submission
  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (doesStringContainPeriod(formValues.fieldName)) {
      setFieldError('Periods cannot be in field names.');
      return;
    }

    const commonData = buildCommonData(fieldId, formValues.fieldName, formValues.dataType);
    // Get the data type configuration for the selected data type and format
    const dataTypeConfig = getConfigByDataType(formValues.dataType, formValues.dataFormat);

    let validationConfigRequestData = formValues.fieldRequired
      ? addRequiredFieldValidator(validationConfig ?? null)
      : removeRequiredFieldValidator(validationConfig ?? null);

    validationConfigRequestData = formValues.currentChargesSumCheck
      ? addCurrentChargesSumCheck(validationConfigRequestData ?? null, formValues.sumcheckConfig)
      : removeCurrentChargesSumCheck(validationConfigRequestData ?? null);

    // Merge common data and additional field data for the API payload
    const updatedFieldData = {
      ...commonData,
      data: {
        ...commonData.data,
        data_type_config: dataTypeConfig,
        ...{ validation_config: validationConfigRequestData },
      },
    };

    // If isReview is true, create a correction
    if (isReview) {
      const content = formValues.fieldValue;
      const tokens: any = []; // Required for the API payload but not used in this context
      const newCorrection = { content, tokens };
      const newDataTypeConfig = formValues.dataType === 'string' ? {} : formValues.dataFormat;

      createCorrectionMutation.mutate({
        extraction: extractionId,
        field: field,
        correction: newCorrection,
        data_type: formValues.dataType,
        data_type_config: newDataTypeConfig,
      });
    } else {
      fieldUpdateMutation.mutate(updatedFieldData);

      // Since the field label could have been renamed, dispatch FOCUS_FIELD so the
      // correct label is focused in the field list
      dispatch({ type: 'FOCUS_FIELD', payload: { field: updatedFieldData?.data?.label } });
    }
  };

  // Handle changes to form fields
  const handleFormFieldChange = (
    // FIXME: fix type
    event: any
    // event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = event.target;
    let newVal = { ...formValues, [name as string]: value as string };

    // If the field being changed is 'dataType', reset 'dataFormat' to {}
    // This is required because a change in data type may necessitate a different data format
    if (name === 'dataType') {
      newVal = { ...newVal, dataFormat: {} };
    }

    // Check for period in field name and set or clear error accordingly
    const error = doesStringContainPeriod(String(value)) ? 'Periods cannot be in field names.' : '';
    setFieldError(error);
    setFormValues(newVal);
  };

  // Handle data format changes
  const handleDataFormatChange = (event: SelectChangeEvent<unknown>) => {
    const newDataFormat = event.target.value as string;

    // Update data format based on the selected data type (e.g., 'date')
    if (formValues.dataType === 'date') {
      setFormValues({
        ...formValues,
        dataFormat: { order: newDataFormat },
      });
    }
    // Update data format for 'amount' or 'number' data types
    // Parse composite format key (e.g., ".|3, 3" -> { decimalSymbol: '.', thousandsGrouping: '3, 3' })
    else if (formValues.dataType === 'amount' || formValues.dataType === 'number') {
      const { decimalSymbol, thousandsGrouping } = parseCompositeFormatKey(newDataFormat);
      setFormValues({
        ...formValues,
        dataFormat: { decimalSymbol, thousandsGrouping },
      });
    }
  };

  const handleFieldValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFormValues((prevValues) => ({
      ...prevValues,
      fieldValue: value,
    }));
  };

  return (
    <DialogFieldStyled
      data-cy="fields-properties-modal"
      open={openModal}
      onClose={handleClose}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={DraggablePaper}
      PaperProps={{ id: `fields-properties-modal-${(isReview ? ebId : tsId) || 'unknown'}` }}
      sx={{
        '& .MuiBackdrop-root': {
          outline: 'none',
        },
      }}
    >
      <DivDialogTitle id="draggable-dialog-title" style={{ cursor: 'move', paddingBottom: '10px' }}>
        <Typography variant="h6">Field Properties</Typography>
      </DivDialogTitle>

      <form onSubmit={handleFormSubmit}>
        <DialogContent>
          {/* Field Name */}
          <DivExtraMargin>
            <DivFlexContainer>
              <DivFlexChild>
                <TextField
                  data-cy="fieldName-markModal"
                  id="editModal-field-value"
                  label="Field Name"
                  name="fieldName"
                  value={formValues.fieldName}
                  onChange={handleFormFieldChange}
                  disabled={isReview}
                  fullWidth
                  inputProps={{ 'data-cy': 'fieldName-markModal-input' }}
                  error={Boolean(fieldError)}
                />
                {fieldError && <Typography color="error">{fieldError}</Typography>}
              </DivFlexChild>
              <DivFlexChild>
                <TextField
                  data-cy="editModal-field-value"
                  id="editModal-field-value"
                  label="Field Value"
                  name="fieldValue"
                  value={isReview ? formValues.fieldValue : initValue}
                  onChange={handleFieldValueChange}
                  disabled={!isReview || isAdditionalField}
                  fullWidth
                />
              </DivFlexChild>
            </DivFlexContainer>
          </DivExtraMargin>

          {/* Data Type & Format */}
          <DivExtraMargin>
            <DivFlexContainer>
              <DivFlexChild>
                {/* FIXME: Implements component <DataTypeSelect /> here */}
                <InputLabelDataType id="data-type-label">Data Type</InputLabelDataType>
                <SelectFormControl
                  data-cy="data-type-selector"
                  id="data-type-selector"
                  labelId="data-type-label"
                  label="Data Type"
                  name="dataType"
                  value={formValues.dataType}
                  onChange={handleFormFieldChange}
                  fullWidth
                  disabled={isAdditionalField}
                >
                  {dataTypesResponse?.map((dataTypeOption, index) => (
                    <MenuItem
                      key={index}
                      value={dataTypeOption.name}
                      data-cy="data-type-selector-item"
                      id="data-type-selector-item"
                    >
                      {capitalizeFirstLetter(dataTypeOption.name)}
                    </MenuItem>
                  ))}
                </SelectFormControl>
              </DivFlexChild>
              <DivFlexChild>
                {formValues.dataType !== 'string' ? (
                  <DataFormatSelect
                    dataTypesResponse={dataTypesResponse}
                    fieldId={fieldId}
                    dataType={formValues.dataType}
                    dataFormat={formValues.dataFormat}
                    handleDataFormatChange={handleDataFormatChange}
                    readOnly={isAdditionalField}
                  />
                ) : null}
              </DivFlexChild>
            </DivFlexContainer>
          </DivExtraMargin>

          {/* Glynt Tags */}
          <DivExtraMargin>
            <InputLabelDataType id="tag-label">Glynt Tags</InputLabelDataType>
            <br />
            {isReview ? (
              <CorrectionFieldTags
                extractionId={extractionId}
                fieldContent={formValues.fieldValue}
                fieldTags={glyntTags}
                fieldLabel={field}
                isReadOnly={isAdditionalField}
              />
            ) : (
              <FieldTags
                fieldTags={glyntTags}
                fieldId={fieldId}
                fieldLabel={field}
                getFieldDataTypeAndConfig={() =>
                  formatDataTypeAndConfig(fieldDataType, fieldDataTypeConfig)
                }
                isGlyntTag={true}
                trainingSetId={tsId}
              />
            )}
          </DivExtraMargin>

          <DivExtraMargin>
            <InputLabelDataType id="tag-label">Validation Rules</InputLabelDataType>
            <br />

            <DivFlexChild>
              <Box sx={{ width: '100%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) =>
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          fieldRequired: e.target.checked,
                        }))
                      }
                      checked={formValues.fieldRequired}
                      disabled={isReview}
                      size="small"
                      color="primary"
                      data-cy="checkbox-mark-modal-required"
                    />
                  }
                  label="Required"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                  }}
                />
                <br />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) =>
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          currentChargesSumCheck: e.target.checked,
                        }))
                      }
                      checked={formValues.currentChargesSumCheck}
                      disabled={isReview}
                      size="small"
                      color="primary"
                      data-cy="checkbox-mark-modal-sumcheck"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Charges Sumcheck</span>
                      {formValues.currentChargesSumCheck && !isReview && (
                        <Tooltip title="Configure include/exclude patterns" placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              setSumcheckConfigDialogOpen(true);
                            }}
                            data-cy="btn-configure-sumcheck"
                            sx={{ p: 0.25 }}
                          >
                            <EditIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {formValues.currentChargesSumCheck && hasCustomSumcheckConfig && (
                        <Typography
                          component="span"
                          variant="caption"
                          onClick={(e) => e.preventDefault()}
                          data-cy="badge-sumcheck-custom"
                          sx={{
                            ml: 0.5,
                            px: 0.75,
                            py: 0.25,
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            borderRadius: 1,
                            fontSize: '0.65rem',
                            fontWeight: 500,
                          }}
                        >
                          Custom
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                  }}
                />
              </Box>
            </DivFlexChild>
          </DivExtraMargin>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            {!isReview ? <DeleteField fieldId={fieldId} fieldLabel={field} trainingSetId={tsId} /> : <div></div>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleClose} data-cy="btn-close-markModal">
                Close
              </Button>
              <Tooltip
                data-cy={`tooltop-modal-additionalField-${field}`}
                title={isReview ? 'Field editing is enabled for additional fields only.' : null}
                placement="top"
                arrow
              >
                <span>
                  <Button
                    color="primary"
                    type="submit"
                    data-cy="btn-save-markModal"
                    disabled={Boolean(fieldError) || isAdditionalField}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </DialogActions>
      </form>

      <ChargesSumcheckConfigDialog
        open={sumcheckConfigDialogOpen}
        config={
          formValues.sumcheckConfig || {
            include: [...DEFAULT_SUMCHECK_CONFIG.include],
            exclude: [...DEFAULT_SUMCHECK_CONFIG.exclude],
          }
        }
        onClose={() => setSumcheckConfigDialogOpen(false)}
        onSave={(newConfig) => {
          setFormValues((prevValues) => ({
            ...prevValues,
            sumcheckConfig: newConfig,
          }));
        }}
      />

      <Dialog
        open={unsavedChangesDialogOpen}
        onClose={() => setUnsavedChangesDialogOpen(false)}
        aria-labelledby="unsaved-changes-dialog-title"
        data-cy="field-modal-unsaved-changes-dialog"
      >
        <DialogTitle id="unsaved-changes-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnsavedChangesDialogOpen(false)}
            data-cy="btn-cancel-unsaved-changes"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClose}
            color="primary"
            data-cy="btn-confirm-unsaved-changes"
          >
            Close Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </DialogFieldStyled>
  );
};

export default FieldModal;
